/**
 * Pearson correlation over paired scores.
 *
 * Used by the deep-analysis correlation matrix and pair scatterplots.
 * Pure function — no Firebase imports.
 */

/**
 * @typedef {object} CorrelationResult
 * @property {number|null} r - Pearson correlation coefficient, or null when not computable (insufficient pairs or zero variance)
 * @property {number} n - Number of valid pairs used
 * @property {boolean} insufficient - True when n is below the minimum number of pairs
 */

// Below this number of pairs a correlation is statistically meaningless for
// our use case — render "onvoldoende data" instead of a misleading r.
export const DEFAULT_MIN_PAIRS = 10

/**
 * @param {Array<{x: number, y: number}>} pairs - Paired scores, one entry per respondent
 * @param {object} [options]
 * @param {number} [options.minPairs] - Minimum pairs before r is computed
 * @returns {CorrelationResult}
 */
const pearsonCorrelation = (pairs, { minPairs = DEFAULT_MIN_PAIRS } = {}) => {
    const clean = (Array.isArray(pairs) ? pairs : []).filter(
        (p) => p != null && Number.isFinite(p.x) && Number.isFinite(p.y)
    )
    const n = clean.length

    if (n < minPairs) return { r: null, n, insufficient: true }

    const meanX = clean.reduce((sum, p) => sum + p.x, 0) / n
    const meanY = clean.reduce((sum, p) => sum + p.y, 0) / n

    let covSum = 0
    let sumDx2 = 0
    let sumDy2 = 0
    clean.forEach((p) => {
        const dx = p.x - meanX
        const dy = p.y - meanY
        covSum += dx * dy
        sumDx2 += dx * dx
        sumDy2 += dy * dy
    })

    // Zero variance on either side → r is undefined, never NaN
    if (sumDx2 === 0 || sumDy2 === 0) return { r: null, n, insufficient: false }

    return { r: covSum / Math.sqrt(sumDx2 * sumDy2), n, insufficient: false }
}

export default pearsonCorrelation
